import { Button } from "../ui";
import {
  fallbackProductImage,
  handleProductImageError,
  normalizeProductImage,
} from "../lib/fallback-image";
import { useLabel } from "../lib/labels";
import { formatWidgetDate, useQtyLabel } from "../lib/orderLabels";
import { navigateExternal } from "../lib/postmessage";
import { useSkuCopyToast } from "../lib/skuCopyToast";
import type {
  OrderBundleChild,
  OrderItem,
  OrderList,
  OrderSummary,
} from "../types";
import DeliveryStepBar from "./DeliveryStepBar";

interface Props {
  data: OrderList;
  sessionToken: string;
}

const _VIEW_DETAIL_LABEL = "View Details";
const _EMPTY_ORDERS_LABEL = "No orders found.";

// EW/Service 블록 — 진행바 없이 상태 라벨 + Model/Serial (product-cases 시안 01)
const _SERVICE_ITEM_TYPES = new Set(["ew", "ews", "ewa", "service"]);

function formatStatusLabel(status: string, label?: string): string {
  if (label) return label;
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// AISASSIT-172(재오픈): 주문내역에 가격 미노출(기획) — formatItemPrice 제거.

// 중첩 자식 1건 (Add-on related·PTO 번들 자식·EW 결합 공통).
// 배송형: Estimated delivery + 5단계 진행바, 서비스(EW)형: 상태 라벨만 (시안 04/07/09).
function NestedChildRow({
  child,
  order,
  onCopy,
  languageCode,
}: {
  child: OrderItem | OrderBundleChild;
  order: OrderSummary;
  onCopy: (sku: string) => void;
  languageCode: string;
}) {
  const estDeliveryLabel = useLabel("estimatedDelivery", "Estimated delivery");
  const qtyText = useQtyLabel(child.quantity);
  const asItem = child as OrderItem;
  const isService = !!asItem.itemType && _SERVICE_ITEM_TYPES.has(asItem.itemType);
  const est = asItem.estimatedDelivery;
  const step = child.deliveryStep;
  const statusText = child.statusLabel;
  const options = asItem.options;
  const imageSrc = child.imageUrl
    ? normalizeProductImage(child.imageUrl)
    : fallbackProductImage();
  const showHead = (!isService && (est || step != null)) || !!statusText;
  return (
    <li className="order-item__nest-item">
      {showHead && (
        <div className="order-item__nest-head">
          {!isService && est ? (
            <strong className="order-item__est">
              {estDeliveryLabel}{" "}
              <span className="order-item__est-date">
                {formatWidgetDate(est, languageCode)}
              </span>
            </strong>
          ) : statusText ? (
            <strong className="order-item__status-title">{statusText}</strong>
          ) : null}
          {!isService && step != null && (
            <div className="order-item__bar">
              <DeliveryStepBar step={step} compact />
            </div>
          )}
        </div>
      )}
      <div className="order-item__nest-row">
        <div className="product-img">
          <img
            src={imageSrc}
            alt={child.name}
            loading="lazy"
            draggable={false}
            onError={handleProductImageError}
          />
        </div>
        <div className="product-inner">
          {child.sku && (
            <div className="product-sku">
              <button
                type="button"
                onClick={() => onCopy(child.sku)}
                title={`Copy: ${child.sku}`}
              >
                {child.sku}
              </button>
            </div>
          )}
          <div className="product-ufn">{child.name}</div>
          {options && options.length > 0 ? (
            <div className="product-info">
              {options.map((opt, i) => (
                <span key={i}>{opt}</span>
              ))}
            </div>
          ) : qtyText ? (
            <div className="product-info">
              <span>{qtyText}</span>
            </div>
          ) : null}
          {/* AISASSIT-172(재오픈): 주문내역에 가격 미노출(기획). */}
        </div>
      </div>
    </li>
  );
}

function OrderPickItem({
  item,
  order,
  highlighted,
  languageCode,
  sessionToken,
}: {
  item: OrderItem;
  order: OrderSummary;
  highlighted: boolean;
  languageCode: string;
  sessionToken: string;
}) {
  const { copySku } = useSkuCopyToast();
  const estDeliveryLabel = useLabel("estimatedDelivery", "Estimated delivery");
  const trackLabel = useLabel("trackMyOrder", "Track My Order");
  const returnLabel = useLabel("return", "Return");
  const inThisBundleLabel = useLabel("inThisBundle", "In this bundle");
  const addOnLabel = useLabel("addOn", "Add-on");
  const modelNumberLabel = useLabel("modelNumber", "Model Number");
  const serialNumberLabel = useLabel("serialNumber", "Serial Number");
  const qtyText = useQtyLabel(item.quantity);
  const isService = !!item.itemType && _SERVICE_ITEM_TYPES.has(item.itemType);
  // AISASSIT-172: 주문내역 항목에는 강조(핑크 그라데이션 보더) 디자인이 없다.
  // productHint 매칭으로 들어온 item.highlighted 를 시각 강조에 쓰지 않는다(정렬만 백엔드 유지).
  void highlighted;
  const itemClass = "pick-item";
  const statusFallback =
    item.statusLabel ?? order.statusLabel ?? formatStatusLabel(order.status);
  const statusText = useLabel(order.status, statusFallback);
  const imageSrc = item.imageUrl
    ? normalizeProductImage(item.imageUrl)
    : order.imageUrl
      ? normalizeProductImage(order.imageUrl)
      : fallbackProductImage();

  return (
    <li className={itemClass}>
      <div className="pick-item--inner">
        {!(item.bundleChildren && item.bundleChildren.length > 0) && (
          <div className="order-item__head">
            {/* AISASSIT-172: 진행바(deliveryStep)가 있는 배송중 주문에만 Estimated delivery 노출.
               취소·환불 등 deliveryStep 이 없는 주문은 백엔드가 estimatedDelivery 를 남겨두어도
               '배송예정 + 진행바 없음' 모순으로 보였다 → 상태(예: Cancelled) 타이틀로 노출한다. */}
            {!isService && item.estimatedDelivery && item.deliveryStep != null ? (
              <strong className="order-item__est">
                {estDeliveryLabel}{" "}
                <span className="order-item__est-date">
                  {formatWidgetDate(item.estimatedDelivery, languageCode)}
                </span>
              </strong>
            ) : (
              <strong className="order-item__status-title">{statusText}</strong>
            )}
            {!isService && item.trackingUrl && (
              <button
                type="button"
                className="order-item__track"
                onClick={() =>
                  navigateExternal(item.trackingUrl!, undefined, {
                    sessionId: sessionToken,
                  })
                }
              >
                {trackLabel}
                <svg
                  className="order-item__track-arrow"
                  viewBox="0 0 10 10"
                  aria-hidden="true"
                >
                  <path
                    d="M3.5 2L6.5 5L3.5 8"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            {!isService && item.deliveryStep != null && (
              <div className="order-item__bar">
                <DeliveryStepBar step={item.deliveryStep} />
              </div>
            )}
          </div>
        )}
        <div className="pick-product">
          <div className="product-img">
            <img
              src={imageSrc}
              alt={item.name}
              loading="lazy"
              draggable={false}
              onError={handleProductImageError}
            />
          </div>
          <div className="product-inner">
            {item.sku && !isService && (
              <div className="product-sku">
                <button
                  type="button"
                  onClick={() => copySku(item.sku)}
                  title={`Copy: ${item.sku}`}
                >
                  {item.sku}
                </button>
              </div>
            )}
            <div className="product-ufn">{item.name}</div>
            {isService ? (
              <div className="product-info product-info--stack">
                {item.modelNumber && (
                  <span>
                    {modelNumberLabel} : {item.modelNumber}
                  </span>
                )}
                {item.serialNumber && (
                  <span>
                    {serialNumberLabel} : {item.serialNumber}
                  </span>
                )}
              </div>
            ) : item.options && item.options.length > 0 ? (
                <div className="product-info">
                  {item.options.map((opt, i) => (
                    <span key={i}>{opt}</span>
                  ))}
                </div>
              ) : qtyText ? (
                <div className="product-info">
                  <span>{qtyText}</span>
                </div>
              ) : null}
            {/* AISASSIT-172(재오픈): 주문내역에 가격 미노출(기획). */}
          </div>
        </div>
        {item.bundleChildren && item.bundleChildren.length > 0 && (
          <div className="order-item__nest">
            <strong className="order-item__nest-title">{inThisBundleLabel}</strong>
            <ul className="order-item__nest-list">
              {item.bundleChildren.map((child, i) => (
                <NestedChildRow
                  key={`${child.sku}-${i}`}
                  child={child}
                  order={order}
                  onCopy={copySku}
                  languageCode={languageCode}
                />
              ))}
            </ul>
          </div>
        )}
        {item.addons && item.addons.length > 0 && (
          <div className="order-item__nest">
            <strong className="order-item__nest-title">{addOnLabel}</strong>
            <ul className="order-item__nest-list">
              {item.addons.map((child, i) => (
                <NestedChildRow
                  key={`${child.sku}-${i}`}
                  child={child}
                  order={order}
                  onCopy={copySku}
                  languageCode={languageCode}
                />
              ))}
            </ul>
          </div>
        )}
        {item.returns && item.returns.length > 0 && (
          <div className="order-item__return">
            <strong className="order-item__return-title">{returnLabel}</strong>
            <div className="order-item__return-lines">
              {item.returns.map((line, i) => (
                <span key={i}>{line}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </li>
  );
}

// 진성 Package Deal(시안 08): packageDealId 가 같은 top-level 아이템을 "Package Deal" 박스로 묶음.
type RenderUnit =
  | { type: "single"; item: OrderItem }
  | { type: "packageDeal"; id: string; items: OrderItem[] };

function groupItemsForRender(items: OrderItem[]): RenderUnit[] {
  const units: RenderUnit[] = [];
  const groups = new Map<string, Extract<RenderUnit, { type: "packageDeal" }>>();
  for (const item of items) {
    if (item.packageDealId) {
      let g = groups.get(item.packageDealId);
      if (!g) {
        g = { type: "packageDeal", id: item.packageDealId, items: [] };
        groups.set(item.packageDealId, g);
        units.push(g);
      }
      g.items.push(item);
    } else {
      units.push({ type: "single", item });
    }
  }
  return units;
}

function OrderGroup({
  order,
  sessionToken,
  languageCode,
}: {
  order: OrderSummary;
  sessionToken: string;
  languageCode: string;
}) {
  const viewDetailLabel = useLabel("viewDetail", _VIEW_DETAIL_LABEL);
  const packageDealLabel = useLabel("packageDeal", "Package Deal");
  const orderNoTpl = useLabel("orderNo", "No. {orderNumber}");
  const orderNoText = orderNoTpl.replace("{orderNumber}", order.orderNumber);
  const dateText = formatWidgetDate(order.placedAt, languageCode, "title");
  const items = order.items.length > 0 ? order.items : [];
  const units = groupItemsForRender(items);

  const handleViewDetail = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (order.orderDetailUrl) {
      navigateExternal(order.orderDetailUrl, undefined, { sessionId: sessionToken });
    }
  };

  return (
    <div className="order-list-wrap">
      <div className="order-list-wrap__head">
        <span className="order-list-wrap__no">{orderNoText}</span>
        <span className="order-list-wrap__date">{dateText}</span>
      </div>
      <ul className="pick-list">
        {units.map((unit, idx) =>
          unit.type === "packageDeal" ? (
            <li className="pick-item pick-item--deal" key={`${order.orderNumber}-deal-${unit.id}`}>
              <strong className="order-deal__title">{packageDealLabel}</strong>
              <ul className="order-deal__list">
                {unit.items.map((item, i) => (
                  <OrderPickItem
                    key={`${order.orderNumber}-${item.sku}-${i}`}
                    item={item}
                    order={order}
                    highlighted={!!item.highlighted}
                    languageCode={languageCode}
                    sessionToken={sessionToken}
                  />
                ))}
              </ul>
            </li>
          ) : (
            <OrderPickItem
              key={`${order.orderNumber}-${unit.item.sku}-${idx}`}
              item={unit.item}
              order={order}
              highlighted={!!unit.item.highlighted}
              languageCode={languageCode}
              sessionToken={sessionToken}
            />
          ),
        )}
      </ul>
      <Button
        variant="secondary"
        size="xs"
        fullWidth
        className="order-list-wrap__detail"
        onClick={handleViewDetail}
      >
        {viewDetailLabel}
      </Button>
    </div>
  );
}

function OrderListEmpty() {
  const emptyLabel = useLabel("noOrders", _EMPTY_ORDERS_LABEL);
  return (
    <div className="ai-gp1--section">
      <div className="ai-gp-order--list">
        <p className="ac-order-list__empty">{emptyLabel}</p>
      </div>
    </div>
  );
}

export default function OrderListCard({ data, sessionToken }: Props) {
  const languageCode = data.languageCode || "en";
  if (!data.orders || data.orders.length === 0) {
    return <OrderListEmpty />;
  }

  return (
    <div className="ai-gp1--section">
      <div className="ai-gp-order--list">
        {data.orders.map((order) => (
          <OrderGroup
            key={order.orderNumber}
            order={order}
            sessionToken={sessionToken}
            languageCode={languageCode}
          />
        ))}
      </div>
    </div>
  );
}
